/**
 * useLiveChats.js
 *
 * Hook for managing live chat conversations.
 * Used by Admin and Client Success dashboards.
 */
import { useState, useEffect, useMemo } from "react";
import { db, getFunctionsClient } from "../firebaseConfig";
import { havloDb, ensureHavloStaffSession } from "../havloFirebaseConfig";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  setDoc,
  deleteDoc,
  arrayUnion,
  serverTimestamp,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

// Havlo workspace ID for TheScope180
const HAVLO_WORKSPACE_ID = "Q6qETEYaE9SxhkqTGos7o1f5hqk2";
export const havloConversationsPath = `workspaces/${HAVLO_WORKSPACE_ID}/conversations`;

const OPEN_STATUSES = new Set(["open", "queued", "active", "waiting"]);

// Havlo's bot sends one of these when a visitor asks for a person and no agent
// is online. Havlo has no needsHumanAttention flag, so this is the signal.
export const HAVLO_HANDOFF_RE =
  /no live agents|connect you with a team member|leave your message|\b(human|real person|live (rep|agent|person)|representative|speak to (someone|a person)|talk to (someone|a person))\b/i;

// Havlo stores timestamps as either Firestore Timestamps or ISO strings.
export function toIso(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// Havlo message `type` -> the `sender` values the dashboard renders
const HAVLO_SENDER = { client: "visitor", bot: "ai", agent: "rep", system: "system" };

function normalizeHavloMessage(id, data) {
  return {
    id,
    ...data,
    sender: data.sender || HAVLO_SENDER[data.type] || "visitor",
    timestamp: toIso(data.createdAt),
  };
}

// Map a Havlo conversation doc to the shape LiveSupportDashboard expects
export function normalizeHavloConversation(id, data) {
  return {
    id,
    ...data,
    source: "havlo",
    shortId: shortChatId(id),
    visitorName: data.visitorName || data.visitorEmail || data.visitorId || "Visitor",
    status: data.status === "open" ? "active" : data.status === "queued" ? "waiting" : data.status,
    isOpen: OPEN_STATUSES.has(data.status),
    humanTakeover: !!data.assignedAgentId,
    needsHumanAttention:
      OPEN_STATUSES.has(data.status) && !data.assignedAgentId && HAVLO_HANDOFF_RE.test(data.lastMessage || ""),
    lastMessageText: data.lastMessage || "",
    lastMessageIso: toIso(data.lastMessageAt) || toIso(data.updatedAt) || toIso(data.createdAt),
  };
}

// Short readable chat ID ("C-7F3K2"), derived from the doc id so it never changes.
// FNV-1a hash -> 5 characters of Crockford base32 (no I, L, O, U).
const SHORT_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
export function shortChatId(id, prefix = "C") {
  let h = 0x811c9dc5;
  const str = String(id || "");
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  let out = "";
  for (let i = 0; i < 5; i++) {
    out += SHORT_ALPHABET[h % 32];
    h = Math.floor(h / 32);
  }
  return `${prefix}-${out}`;
}

// Map a site liveChats doc (LiveChatWidget) to the same shape as Havlo chats
export function normalizeSiteChat(id, data) {
  const msgs = data.messages || [];
  const last = msgs[msgs.length - 1];
  const isOpen = data.status === "active" || data.status === "waiting";
  return {
    id,
    ...data,
    source: "site",
    shortId: shortChatId(id),
    visitorName: data.visitorName || data.userEmail || "Visitor",
    isOpen,
    humanTakeover: !!data.humanTakeover,
    needsHumanAttention: isOpen && !!data.needsHumanAttention && !data.humanTakeover,
    lastMessageText: last?.text || "",
    lastMessageIso: toIso(data.lastMessageAt) || last?.timestamp || toIso(data.startedAt),
  };
}

const chatKey = (source, id) => `${source === "site" ? "site" : "havlo"}_${id}`;

/**
 * Hook to get live chats (Havlo conversations + the site's own liveChats),
 * split into open and closed/archived. Chats an admin hid are left out.
 * Every chat carries `source` ("havlo" | "site") and a `shortId`.
 * @returns {{ chats: Array, closedChats: Array, loading: boolean, activeCount: number, error: string|null }}
 */
export function useLiveChats() {
  const [havloChats, setHavloChats] = useState([]);
  const [siteChats, setSiteChats] = useState([]);
  const [hidden, setHidden] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isCurrent = true;
    const unsubs = [];

    // Timeout fallback to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (isCurrent) setLoading(false);
    }, 3000);

    try {
      // Read from HAVLO Firestore - conversations for our workspace.
      // Sorted client-side: a query orderBy would silently drop conversations
      // missing lastMessageAt. Havlo only lets members list chats, so first
      // open a staff session (havloStaffToken); see havloFirebaseConfig.js.
      ensureHavloStaffSession().then(() => {
        if (!isCurrent) return;
        unsubs.push(onSnapshot(
          collection(havloDb, havloConversationsPath),
          (snapshot) => {
            if (!isCurrent) return;
            setHavloChats(snapshot.docs.map((d) => normalizeHavloConversation(d.id, d.data())));
            setLoading(false);
            setError(null);
          },
          (err) => {
            if (!isCurrent) return;
            // Index not ready or collection doesn't exist - not a critical error
            if (err.code === "failed-precondition" || err.code === "unavailable") {
              console.warn("Live chats: index not ready or empty collection. Check console for index creation link.");
              setHavloChats([]);
            } else {
              console.error("Live chats error:", err);
              setError(err.message);
            }
            setLoading(false);
          }
        ));
      });

      // The site's own chat (LiveChatWidget -> liveChats)
      unsubs.push(onSnapshot(
        query(collection(db, "liveChats"), orderBy("lastMessageAt", "desc"), limit(200)),
        (snapshot) => {
          if (!isCurrent) return;
          setSiteChats(snapshot.docs.map((d) => normalizeSiteChat(d.id, d.data())));
          setLoading(false);
        },
        (err) => console.warn("Site live chats unavailable:", err.code || err.message)
      ));

      // Chats an admin hid from the queue (Havlo conversations can't be deleted here)
      unsubs.push(onSnapshot(
        collection(db, "supportHiddenChats"),
        (snapshot) => isCurrent && setHidden(new Set(snapshot.docs.map((d) => d.id))),
        () => {}
      ));
    } catch (err) {
      console.error("Failed to setup live chats listener:", err);
      setLoading(false);
      setError(err.message);
    }

    return () => {
      isCurrent = false;
      clearTimeout(loadingTimeout);
      unsubs.forEach((u) => {
        try { u(); } catch (e) { /* ignore cleanup errors */ }
      });
    };
  }, []);

  const all = useMemo(
    () => [...havloChats, ...siteChats]
      .filter((c) => !hidden.has(chatKey(c.source, c.id)))
      .sort((a, b) => (b.lastMessageIso || "").localeCompare(a.lastMessageIso || "")),
    [havloChats, siteChats, hidden]
  );
  const chats = useMemo(() => all.filter((c) => c.isOpen), [all]);
  const closedChats = useMemo(() => all.filter((c) => !c.isOpen), [all]);

  return {
    chats,
    closedChats,
    loading,
    activeCount: chats.length,
    error,
  };
}

/**
 * Hook to get a single chat conversation with real-time updates
 * @param {string} chatId
 * @param {"havlo"|"site"} [source]
 * @returns {{ chat: Object, messages: Array, loading: boolean }}
 */
export function useChatConversation(chatId, source = "havlo") {
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!chatId) {
      setLoading(false);
      setChat(null);
      return;
    }

    let isCurrent = true;
    let unsubscribe = null;
    setLoading(true);

    try {
      if (source === "site") {
        // Site chats keep their messages in an array on the doc
        unsubscribe = onSnapshot(
          doc(db, "liveChats", chatId),
          (docSnap) => {
            if (!isCurrent) return;
            if (docSnap.exists()) {
              const data = docSnap.data();
              setChat({
                ...normalizeSiteChat(docSnap.id, data),
                messages: (data.messages || []).map((m, idx) => ({ id: m.id || `m-${idx}`, ...m })),
              });
            } else {
              setChat(null);
            }
            setLoading(false);
          },
          (error) => {
            if (!isCurrent) return;
            console.error("Chat conversation error:", error);
            setLoading(false);
          }
        );
      } else {
        // Listen to Havlo conversation doc (after the staff session is open)
        let convoUnsub = () => {};
        let msgsUnsub = () => {};
        ensureHavloStaffSession().then(() => {
          if (!isCurrent) return;
          convoUnsub = onSnapshot(
            doc(havloDb, havloConversationsPath, chatId),
            (docSnap) => {
              if (!isCurrent) return;
              if (docSnap.exists()) {
                const convo = normalizeHavloConversation(docSnap.id, docSnap.data());
                setChat((prev) => ({ ...convo, messages: prev?.id === convo.id ? prev.messages : [] }));
              } else {
                setChat(null);
              }
              setLoading(false);
            },
            (error) => {
              if (!isCurrent) return;
              console.error("Chat conversation error:", error);
              setLoading(false);
            }
          );

          // Also listen to Havlo messages subcollection
          msgsUnsub = onSnapshot(
            // No orderBy: Havlo mixes ISO-string and Timestamp createdAt values, and
            // Firestore sorts those by type first, which scrambles the order.
            collection(havloDb, havloConversationsPath, chatId, "messages"),
            (snap) => {
              if (!isCurrent) return;
              const msgs = snap.docs
                .map((d) => normalizeHavloMessage(d.id, d.data()))
                .sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
              setChat((prev) => ({ ...(prev || { id: chatId }), messages: msgs }));
            },
            (err) => console.error("Chat messages error:", err)
          );
        });

        unsubscribe = () => { convoUnsub(); msgsUnsub(); };
      }
    } catch (err) {
      console.error("Failed to setup chat listener:", err);
      setLoading(false);
    }

    return () => {
      isCurrent = false;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, [chatId, source]);

  return {
    chat,
    messages: chat?.messages || [],
    loading,
  };
}

const systemMessage = (text) => ({
  id: `system-${Date.now()}`,
  text,
  sender: "system",
  senderName: "System",
  timestamp: new Date().toISOString(),
});

/**
 * Take over a chat from AI. The visitor sees "<alias> has joined the
 * conversation." and the bot stops replying.
 * @param {string} chatId
 * @param {string} repId - User ID of the rep taking over
 * @param {string} repDisplayName - The rep's chat alias, e.g. "Sarah W."
 * @param {"havlo"|"site"} [source]
 */
export async function takeOverChat(chatId, repId, repDisplayName, source = "havlo") {
  try {
    if (source === "site") {
      await updateDoc(doc(db, "liveChats", chatId), {
        humanTakeover: true,
        assignedTo: repId,
        assignedName: repDisplayName,
        needsHumanAttention: false,
        takenOverAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        messages: arrayUnion(systemMessage(`${repDisplayName} has joined the conversation.`)),
      });
      return { success: true };
    }
    // Update Havlo conversation
    await ensureHavloStaffSession();
    await updateDoc(doc(havloDb, havloConversationsPath, chatId), {
      assignedAgentId: repId,
      assignedAgentName: repDisplayName,
      status: "open",
      lastMessageAt: serverTimestamp(),
    });
    // Add system message to Havlo messages subcollection
    await addDoc(collection(havloDb, havloConversationsPath, chatId, "messages"), {
      text: `${repDisplayName} has joined the conversation.`,
      type: "system",
      senderId: "system",
      createdAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("Takeover error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Send a message as a support rep
 * @param {string} chatId
 * @param {string} text
 * @param {string} senderId
 * @param {string} senderName
 * @param {"havlo"|"site"} [source]
 */
export async function sendRepMessage(chatId, text, senderId, senderName, source = "havlo") {
  try {
    if (source === "site") {
      await updateDoc(doc(db, "liveChats", chatId), {
        messages: arrayUnion({
          id: `rep-${Date.now()}`,
          text,
          sender: "rep",
          senderId,
          senderName,
          timestamp: new Date().toISOString(),
        }),
        lastMessageAt: serverTimestamp(),
      });
      return { success: true };
    }
    // Add message to Havlo messages subcollection
    await ensureHavloStaffSession();
    await addDoc(collection(havloDb, havloConversationsPath, chatId, "messages"), {
      text,
      type: "agent",
      senderId,
      senderName,
      createdAt: serverTimestamp(),
    });
    // Update conversation lastMessageAt
    await updateDoc(doc(havloDb, havloConversationsPath, chatId), {
      lastMessageAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("Send message error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Get 3 suggested replies for the rep (written as a person, never as an AI)
 * @param {Array} messages
 * @returns {Promise<Array<string>>}
 */
export async function getSuggestedReplies(messages) {
  try {
    const chatSupportAI = httpsCallable(getFunctionsClient(), "chatSupportAI");
    const result = await chatSupportAI({
      action: "suggestReplies",
      messages: (messages || []).slice(-12).map((m) => ({ sender: m.sender, senderName: m.senderName || null, text: m.text || "" })),
    });
    return (result.data?.suggestions || []).slice(0, 3);
  } catch (error) {
    console.error("Suggested replies error:", error);
    return [
      "Thank you for your patience, how can I help?",
      "Happy to help with that! Could you tell me a little more?",
      "Let me check on that for you, one moment please.",
    ];
  }
}

/**
 * Close/resolve a chat
 * @param {string} chatId
 * @param {"havlo"|"site"} [source]
 */
export async function closeChat(chatId, source = "havlo") {
  try {
    if (source === "site") {
      await updateDoc(doc(db, "liveChats", chatId), {
        status: "closed",
        needsHumanAttention: false,
        closedAt: serverTimestamp(),
        messages: arrayUnion(systemMessage("This conversation has been closed. Thank you for chatting with us!")),
      });
      return { success: true };
    }
    await ensureHavloStaffSession();
    await updateDoc(doc(havloDb, havloConversationsPath, chatId), {
      status: "closed",
      closedAt: serverTimestamp(),
    });
    await addDoc(collection(havloDb, havloConversationsPath, chatId, "messages"), {
      text: "This conversation has been closed. Thank you for chatting with us!",
      type: "system",
      senderId: "system",
      createdAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("Close chat error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Remove a chat from the Support Queue (admins only).
 * Site chats are deleted (firestore.rules lets admins delete liveChats).
 * Havlo conversations can't be deleted from this site (Havlo's rules allow no
 * conversation delete), so they are hidden: supportHiddenChats/havlo_{id}.
 * @param {{ id: string, source: string, visitorName?: string, shortId?: string }} chat
 * @param {string} adminUid
 * @returns {Promise<{ success: boolean, mode?: "deleted"|"hidden", error?: string }>}
 */
export async function removeChatFromQueue(chat, adminUid) {
  try {
    if (chat.source === "site") {
      await deleteDoc(doc(db, "liveChats", chat.id));
      return { success: true, mode: "deleted" };
    }
    await setDoc(doc(db, "supportHiddenChats", chatKey("havlo", chat.id)), {
      source: "havlo",
      chatId: chat.id,
      shortId: chat.shortId || shortChatId(chat.id),
      visitorName: chat.visitorName || null,
      hiddenBy: adminUid,
      hiddenAt: serverTimestamp(),
    });
    return { success: true, mode: "hidden" };
  } catch (error) {
    console.error("Remove chat error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Create an outbound chat initiated by a support rep
 * @param {{ repId: string, repName: string, userId: string, userName: string, userEmail: string, initialMessage: string }}
 * @returns {Promise<string>} The new chat document ID
 */
export async function createOutboundChat({ repId, repName, userId, userName, userEmail, initialMessage }) {
  const docRef = await addDoc(collection(db, "liveChats"), {
    visitorId: `outbound_${userId}`,
    visitorName: userName,
    userId,
    userEmail,
    status: "active",
    origin: "outbound",
    humanTakeover: true,
    assignedTo: repId,
    assignedName: repName,
    aiMessageCount: 0,
    messages: [{
      id: `rep-${Date.now()}`,
      text: initialMessage,
      sender: "rep",
      senderId: repId,
      senderName: repName,
      timestamp: new Date().toISOString(),
    }],
    startedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
  });
  return docRef.id;
}

export default useLiveChats;
